import { jwtDecode } from 'jwt-decode'
import Cookies from 'js-cookie'

/**
 * Token storage keys
 */
const TOKEN_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  ID_TOKEN: 'id_token',
} as const

/**
 * Cookie options for secure token storage
 */
const COOKIE_OPTIONS: Cookies.CookieAttributes = {
  secure: true, // Only send over HTTPS
  sameSite: 'strict', // CSRF protection
  path: '/',
}

/**
 * JWT token claims interface
 */
interface TokenClaims {
  exp: number
  iat: number
  [key: string]: unknown
}

/**
 * Token Manager
 * 
 * Handles secure storage and retrieval of authentication tokens.
 * Prioritizes httpOnly cookies (via backend API) with fallback to secure sessionStorage.
 * 
 * Security features:
 * - Secure attribute (HTTPS only)
 * - SameSite=Strict (CSRF protection)
 * - No localStorage usage (XSS protection)
 * - Fallback to sessionStorage with secure flags
 */
class TokenManager {
  private useHttpOnlyCookies: boolean = false

  /**
   * Store authentication tokens
   * 
   * Attempts to store in httpOnly cookies via backend API.
   * Falls back to secure sessionStorage if cookies are not available.
   * 
   * @param tokens - Object containing access, refresh, and ID tokens
   */
  async storeTokens(tokens: {
    accessToken: string
    refreshToken: string
    idToken: string
  }): Promise<void> {
    try {
      // Try to store in httpOnly cookies via backend API
      if (this.useHttpOnlyCookies) {
        await this.storeInHttpOnlyCookies(tokens)
      } else {
        // Fallback to secure sessionStorage
        this.storeInSessionStorage(tokens)
      }
    } catch (error) {
      console.error('Error storing tokens:', error)
      // Fallback to sessionStorage on error
      this.storeInSessionStorage(tokens)
    }
  }

  /**
   * Store tokens in httpOnly cookies via backend API
   * 
   * @param tokens - Tokens to store
   */
  private async storeInHttpOnlyCookies(tokens: {
    accessToken: string
    refreshToken: string
    idToken: string
  }): Promise<void> {
    // This would call a backend endpoint that sets httpOnly cookies
    // For now, we'll use js-cookie with secure flags as a compromise
    // In production, the backend should set httpOnly cookies
    
    Cookies.set(TOKEN_KEYS.ACCESS_TOKEN, tokens.accessToken, {
      ...COOKIE_OPTIONS,
      expires: this.getTokenExpiryDays(tokens.accessToken),
    })
    
    Cookies.set(TOKEN_KEYS.REFRESH_TOKEN, tokens.refreshToken, {
      ...COOKIE_OPTIONS,
      expires: 30, // Refresh tokens typically last 30 days
    })
    
    Cookies.set(TOKEN_KEYS.ID_TOKEN, tokens.idToken, {
      ...COOKIE_OPTIONS,
      expires: this.getTokenExpiryDays(tokens.idToken),
    })
  }

  /**
   * Store tokens in secure sessionStorage as fallback
   * 
   * @param tokens - Tokens to store
   */
  private storeInSessionStorage(tokens: {
    accessToken: string
    refreshToken: string
    idToken: string
  }): void {
    // Use sessionStorage (not localStorage) for better security
    // Tokens are cleared when browser tab is closed
    sessionStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, tokens.accessToken)
    sessionStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, tokens.refreshToken)
    sessionStorage.setItem(TOKEN_KEYS.ID_TOKEN, tokens.idToken)
  }

  /**
   * Get access token from storage
   * 
   * @returns Access token or null if not found
   */
  getAccessToken(): string | null {
    // Try cookies first
    const cookieToken = Cookies.get(TOKEN_KEYS.ACCESS_TOKEN)
    if (cookieToken) return cookieToken

    // Fallback to sessionStorage
    return sessionStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN)
  }

  /**
   * Get refresh token from storage
   * 
   * @returns Refresh token or null if not found
   */
  getRefreshToken(): string | null {
    // Try cookies first
    const cookieToken = Cookies.get(TOKEN_KEYS.REFRESH_TOKEN)
    if (cookieToken) return cookieToken

    // Fallback to sessionStorage
    return sessionStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN)
  }

  /**
   * Get ID token from storage
   * 
   * @returns ID token or null if not found
   */
  getIdToken(): string | null {
    // Try cookies first
    const cookieToken = Cookies.get(TOKEN_KEYS.ID_TOKEN)
    if (cookieToken) return cookieToken

    // Fallback to sessionStorage
    return sessionStorage.getItem(TOKEN_KEYS.ID_TOKEN)
  }

  /**
   * Clear all tokens from storage
   */
  clearTokens(): void {
    // Clear cookies
    Cookies.remove(TOKEN_KEYS.ACCESS_TOKEN, { path: '/' })
    Cookies.remove(TOKEN_KEYS.REFRESH_TOKEN, { path: '/' })
    Cookies.remove(TOKEN_KEYS.ID_TOKEN, { path: '/' })

    // Clear sessionStorage
    sessionStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN)
    sessionStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN)
    sessionStorage.removeItem(TOKEN_KEYS.ID_TOKEN)
  }

  /**
   * Check if a token is expired
   * 
   * @param token - JWT token to check
   * @returns true if expired, false otherwise
   */
  isTokenExpired(token: string | null): boolean {
    if (!token) return true

    try {
      const expiry = this.getTokenExpiry(token)
      if (!expiry) return true

      const now = Math.floor(Date.now() / 1000)
      return now >= expiry
    } catch (error) {
      console.error('Error checking token expiration:', error)
      return true
    }
  }

  /**
   * Get token expiry time from JWT
   * 
   * @param token - JWT token
   * @returns Expiry time in seconds (Unix timestamp) or null if not found
   */
  getTokenExpiry(token: string): number | null {
    try {
      const decoded = jwtDecode<TokenClaims>(token)
      return decoded.exp || null
    } catch (error) {
      console.error('Error decoding token:', error)
      return null
    }
  }

  /**
   * Get token expiry in days for cookie expiration
   * 
   * @param token - JWT token
   * @returns Number of days until expiry
   */
  private getTokenExpiryDays(token: string): number {
    const expiry = this.getTokenExpiry(token)
    if (!expiry) return 1 // Default to 1 day

    const now = Math.floor(Date.now() / 1000)
    const secondsUntilExpiry = expiry - now
    const daysUntilExpiry = Math.ceil(secondsUntilExpiry / (60 * 60 * 24))

    return Math.max(daysUntilExpiry, 1) // At least 1 day
  }

  /**
   * Enable httpOnly cookie storage via backend API
   * 
   * @param enabled - Whether to use httpOnly cookies
   */
  setHttpOnlyCookies(enabled: boolean): void {
    this.useHttpOnlyCookies = enabled
  }
}

// Export singleton instance
export const tokenManager = new TokenManager()
